import { useGlobalReservedAppBarHeight } from '@/components/GlobalNavigationDrawer';
import { useReservedAppBarHeight } from '@/components/NavigationDrawer/NavigationDrawer';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton, SxProps, Theme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { RefObject, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Fraction of the visible width a single arrow click travels.
const SCROLL_STEP_RATIO = 0.8;

// The floating scroll arrows, portaled to the body so their fixed position stays
// viewport-relative past any clipping or transformed ancestor. Circular white
// buttons matching the tab-bar scroll arrows, hidden until positioned.
const scrollArrowSx: SxProps<Theme> = (theme) => ({
    position: 'fixed',
    display: 'none',
    zIndex: theme.zIndex.appBar,
    width: 28,
    height: 28,
    padding: 0,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${grey[300]}`,
    boxShadow: theme.shadows[2],
    '&:hover': { backgroundColor: grey[100] },
});

// The nearest scrolling ancestor: the element the header pins against.
const findVerticalScroller = (start: HTMLElement | null): HTMLElement | null => {
    for (let el = start; el; el = el.parentElement) {
        const { overflowY } = getComputedStyle(el);
        if (/(auto|scroll|overlay)/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) {
            return el;
        }
    }
    return null; // body-scroll page → pin to the viewport top (+ app-bar offset)
};

/**
 * Table "chrome" that has to track scroll: a header pinned to the top of the
 * page as it scrolls, and left/right arrow buttons that scroll a wide table
 * horizontally without scrolling to the bottom to reach a scrollbar. Both are
 * driven from ONE scroll loop that computes the header's offset once, so the
 * pinned header and the arrows can never drift apart.
 *
 * The header pin is a JS `translateY` rather than `position: sticky` because the
 * table sits inside a wrapper that scrolls on both axes, a scrollport a sticky
 * header can't escape. The arrows are portaled to the body and float over the
 * left/right ends of the header row, each shown only while there is distance to
 * scroll in that direction.
 *
 * The hook owns its DOM plumbing and returns the container ref to attach plus
 * the arrow elements to render (null when horizontal scroll is off). The
 * container is required either way: it anchors the geometry observers and the
 * search for the vertical scroller the header pins against.
 *
 * With `pinnedColumn` set, the same loop marks the container with
 * `data-pinned-overflow` while columns remain hidden to the right, so a sticky
 * column can drop its divider and depth cue once it covers nothing.
 *
 * `scrollContainer` names the horizontally scrolling element inside it: a ref
 * when the caller owns that node, or a selector matched within the container
 * when the node belongs to a table library.
 */
export const useStickyTableChrome = ({
    stickyHeader,
    horizontalScrollButtons,
    pinnedColumn = false,
    scrollContainer,
}: {
    stickyHeader: boolean;
    horizontalScrollButtons: boolean;
    pinnedColumn?: boolean;
    scrollContainer: RefObject<HTMLElement | null> | string;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const leftArrowRef = useRef<HTMLButtonElement>(null);
    const rightArrowRef = useRef<HTMLButtonElement>(null);

    // Distance from the viewport top to pin the header at when the body is the
    // scroller (a fixed mobile app bar); '0px' on desktop. Same source the two
    // legacy sticky-header call sites used, computed once here.
    const hasGlobalNavV2 = useFeatureFlagEnabled(FeatureFlag.GLOBAL_NAVIGATION_V2);
    const globalAppBarHeight = useGlobalReservedAppBarHeight();
    const legacyAppBarHeight = useReservedAppBarHeight();
    const topOffset = parseInt(hasGlobalNavV2 ? globalAppBarHeight : legacyAppBarHeight, 10) || 0;

    // Scroll the table one viewport-fraction in either direction; the resulting
    // scroll event repositions the arrows and refreshes their visibility.
    const resolveScroller = (wrapper: HTMLElement | null) =>
        typeof scrollContainer === 'string'
            ? wrapper?.querySelector<HTMLElement>(scrollContainer) ?? null
            : scrollContainer.current;

    const scrollBy = (direction: 1 | -1) => {
        const scroller = resolveScroller(containerRef.current);
        if (scroller) {
            scroller.scrollBy({
                left: direction * scroller.clientWidth * SCROLL_STEP_RATIO,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        const wrapper = containerRef.current;
        if ((!stickyHeader && !horizontalScrollButtons && !pinnedColumn) || !wrapper) {
            return;
        }
        const leftArrow = leftArrowRef.current;
        const rightArrow = rightArrowRef.current;
        let frame = 0;
        let lastThead: HTMLElement | null = null;

        const apply = () => {
            frame = 0;
            const scroller =
                typeof scrollContainer === 'string'
                    ? wrapper.querySelector<HTMLElement>(scrollContainer)
                    : scrollContainer.current;
            const table = scroller?.querySelector('table');
            const thead = scroller?.querySelector<HTMLElement>('table thead');
            if (!scroller || !table || !thead) {
                if (leftArrow) {
                    leftArrow.style.display = 'none';
                }
                if (rightArrow) {
                    rightArrow.style.display = 'none';
                }
                if (pinnedColumn) {
                    wrapper.dataset.pinnedOverflow = 'false';
                }
                return;
            }

            // One offset for both features: how far the header is pushed down from
            // the table's natural top to stay pinned, clamped so it never travels
            // past the table's bottom edge (like native sticky).
            const vScroller = findVerticalScroller(wrapper.parentElement);
            const pinLine = vScroller
                ? vScroller.getBoundingClientRect().top + vScroller.clientTop
                : topOffset;
            const tableRect = table.getBoundingClientRect();
            const headerHeight = thead.offsetHeight;
            const overflow = scroller.scrollWidth - scroller.clientWidth;
            const moreToRight = overflow > 1 && scroller.scrollLeft < overflow - 1;

            if (pinnedColumn) {
                wrapper.dataset.pinnedOverflow = String(moreToRight);
            }

            const maxTravel = Math.max(0, tableRect.height - headerHeight);
            const offset = Math.min(Math.max(0, pinLine - tableRect.top), maxTravel);

            if (stickyHeader) {
                // Remounts replace the thead node; clear the old one.
                if (lastThead && lastThead !== thead) {
                    lastThead.style.removeProperty('transform');
                }
                lastThead = thead;
                thead.style.transform = offset > 0.5 ? `translateY(${offset}px)` : '';
            }

            if (horizontalScrollButtons && leftArrow && rightArrow) {
                // The arrows track the header wherever it currently is: with the
                // pin applied when stickyHeader is on, at the natural top otherwise.
                const headerTop = tableRect.top + (stickyHeader ? offset : 0);
                const headerBottom = headerTop + headerHeight;
                const onScreen =
                    tableRect.top < window.innerHeight &&
                    tableRect.bottom > headerBottom + 1 &&
                    headerBottom < window.innerHeight;
                // Vertically center each arrow on the header row.
                const centerY = headerTop + headerHeight / 2;
                const scrollerRect = scroller.getBoundingClientRect();

                const showLeft = overflow > 1 && onScreen && scroller.scrollLeft > 1;
                const showRight = moreToRight && onScreen;

                if (showLeft) {
                    leftArrow.style.display = 'flex';
                    leftArrow.style.top = `${centerY - leftArrow.offsetHeight / 2}px`;
                    leftArrow.style.left = `${scrollerRect.left + 4}px`;
                } else {
                    leftArrow.style.display = 'none';
                }

                if (showRight) {
                    rightArrow.style.display = 'flex';
                    rightArrow.style.top = `${centerY - rightArrow.offsetHeight / 2}px`;
                    rightArrow.style.left = `${scrollerRect.right - rightArrow.offsetWidth - 4}px`;
                } else {
                    rightArrow.style.display = 'none';
                }
            }
        };

        const schedule = () => {
            if (!frame) {
                frame = requestAnimationFrame(apply);
            }
        };

        // Apply synchronously on scroll (pre-paint) so the header and arrows stay
        // visually locked to the table as it moves.
        const onScrollCapture = () => {
            if (frame) {
                cancelAnimationFrame(frame);
                frame = 0;
            }
            apply();
        };

        // Capture on window catches the table's own scroll and any ancestor
        // scroller (scroll events don't bubble) without holding a node reference.
        window.addEventListener('scroll', onScrollCapture, { capture: true, passive: true });
        window.addEventListener('resize', schedule);
        // Remounts swap the table node; column/row size changes move the geometry.
        // apply() only writes style attributes, so neither observer self-triggers.
        const mutations = new MutationObserver(schedule);
        mutations.observe(wrapper, { childList: true, subtree: true });
        const resizes = new ResizeObserver(schedule);
        resizes.observe(wrapper);
        schedule();

        return () => {
            window.removeEventListener('scroll', onScrollCapture, { capture: true });
            window.removeEventListener('resize', schedule);
            mutations.disconnect();
            resizes.disconnect();
            if (frame) {
                cancelAnimationFrame(frame);
            }
            lastThead?.style.removeProperty('transform');
            delete wrapper.dataset.pinnedOverflow;
        };
    }, [stickyHeader, horizontalScrollButtons, pinnedColumn, topOffset, scrollContainer]);

    /* The portal is wrapped in a fragment so consumers hand an element to whatever
     * renders it: the `node` prop-type MUI validates `children` against rejects a bare
     * portal, though React renders one.
     */
    const scrollButtons = horizontalScrollButtons ? (
        <>
            {createPortal(
                <>
                    <IconButton
                        ref={leftArrowRef}
                        sx={scrollArrowSx}
                        onClick={() => scrollBy(-1)}
                        aria-label='Scroll table left'
                        title='Scroll table left'
                        data-testid='axle-table-scroll-left'
                    >
                        <ChevronLeft sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                        ref={rightArrowRef}
                        sx={scrollArrowSx}
                        onClick={() => scrollBy(1)}
                        aria-label='Scroll table right'
                        title='Scroll table right'
                        data-testid='axle-table-scroll-right'
                    >
                        <ChevronRight sx={{ fontSize: 18 }} />
                    </IconButton>
                </>,
                document.body,
            )}
        </>
    ) : null;

    return { containerRef, scrollButtons };
};
